const DEFAULT_SPORT_FOR_AGE = 'flag';
const VALID_RESTRICTION_TYPES = new Set(['estricta', 'recomendada', 'sin_restriccion']);

const normalizeSport = (sport) =>
  sport ? sport.toString().trim().toLowerCase() : DEFAULT_SPORT_FOR_AGE;

export const normalizeRestrictionType = (value, fallback = 'estricta') => {
  const normalized = (value || '').toString().trim().toLowerCase();
  return VALID_RESTRICTION_TYPES.has(normalized) ? normalized : fallback;
};

export const getGroupRestrictionType = (group) => {
  const direct = normalizeRestrictionType(group?.tipo_restriccion, '');
  if (direct) return direct;

  const fromCategories = Array.isArray(group?.categorias)
    ? group.categorias
        .map((cat) => normalizeRestrictionType(cat?.tipo_restriccion, ''))
        .filter(Boolean)
    : [];

  if (fromCategories.includes('estricta')) return 'estricta';
  if (fromCategories.includes('recomendada')) return 'recomendada';
  if (fromCategories.includes('sin_restriccion')) return 'sin_restriccion';
  return 'estricta';
};

export const calculateAge = (dateString) => {
  if (!dateString) return null;
  const birthDate = new Date(dateString);
  if (Number.isNaN(birthDate.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age -= 1;
  }
  return age;
};

export const calculateAgeForSport = (dateString, sport = DEFAULT_SPORT_FOR_AGE, referenceDate) => {
  if (!dateString) return null;
  const birthDate = new Date(dateString);
  if (Number.isNaN(birthDate.getTime())) return null;

  const normalizedSport = normalizeSport(sport);
  if (normalizedSport !== 'flag') {
    return calculateAge(dateString);
  }

  const today = referenceDate instanceof Date ? referenceDate : new Date();
  const seasonEnd = new Date(today.getFullYear(), 11, 31);
  let age = seasonEnd.getFullYear() - birthDate.getFullYear();
  const monthDiff = seasonEnd.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && seasonEnd.getDate() < birthDate.getDate())) {
    age -= 1;
  }
  return age;
};

export const parseAgeInput = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return Math.floor(parsed);
};

export const normalizeAgePayload = (state) => {
  const restrict = Boolean(state?.restringir_por_edad);
  if (!restrict) {
    return {
      restringir_por_edad: false,
      edad_minima: null,
      edad_maxima: null
    };
  }
  return {
    restringir_por_edad: true,
    edad_minima: parseAgeInput(state?.edad_minima),
    edad_maxima: parseAgeInput(state?.edad_maxima)
  };
};

export const validateAgeLimits = (state) => {
  if (!state?.restringir_por_edad) {
    return { valid: true };
  }
  const min = parseAgeInput(state.edad_minima);
  const max = parseAgeInput(state.edad_maxima);
  if (min === null && max === null) {
    return {
      valid: false,
      message: 'Define al menos una edad mínima o máxima para restringir el grupo.'
    };
  }
  if (min !== null && max !== null && min > max) {
    return {
      valid: false,
      message: 'La edad mínima no puede ser mayor que la edad máxima.'
    };
  }
  return { valid: true };
};

export const extractGroupAgeLimits = (group) => {
  if (!group?.restringir_por_edad) return null;
  const min = Number.isFinite(group.edad_minima) ? group.edad_minima : null;
  const max = Number.isFinite(group.edad_maxima) ? group.edad_maxima : null;
  return { min, max };
};

export const getAgeLimitStatus = (dateString, limits, options = {}) => {
  const { sport = DEFAULT_SPORT_FOR_AGE, referenceDate = new Date() } = options;
  if (!limits) {
    return {
      allowed: true,
      age: calculateAge(dateString),
      belowMin: false,
      aboveMax: false,
      warningBelowMin: false,
      recommendedMin: null,
      missingBirthdate: !dateString
    };
  }

  const age = calculateAgeForSport(dateString, sport, referenceDate);
  if (age === null) {
    return {
      allowed: false,
      age: null,
      belowMin: false,
      aboveMax: false,
      warningBelowMin: false,
      recommendedMin: null,
      missingBirthdate: true
    };
  }

  const normalizedSport = normalizeSport(sport);
  let belowMin = limits.min !== null && age < limits.min;
  const aboveMax = limits.max !== null && age > limits.max;
  let warningBelowMin = false;
  let recommendedMin = null;

  if (normalizedSport === 'flag') {
    // En flag el límite inferior es solo recomendado (2 años por debajo del máximo o mínimo definido)
    const base = limits.max !== null ? limits.max : limits.min;
    if (base !== null) {
      recommendedMin = Math.max(base - 2, 0);
      if (age < recommendedMin) {
        warningBelowMin = true;
      }
    }
    belowMin = false; // nunca bloqueamos por mínima en flag
  } else if (limits.min !== null) {
    recommendedMin = limits.min;
  }

  return {
    allowed: !belowMin && !aboveMax,
    age,
    belowMin,
    aboveMax,
    warningBelowMin,
    recommendedMin,
    missingBirthdate: false
  };
};

export const isAgeAllowed = (dateString, limits, options = {}) => {
  if (!limits) return true;
  return getAgeLimitStatus(dateString, limits, options).allowed;
};

export const formatAgeLimitsLabel = (limits) => {
  if (!limits) return 'Sin restricción por edad';
  const { min, max } = limits;
  if (min !== null && max !== null) {
    return `${min}-${max} años`;
  }
  if (min !== null) {
    return `Desde ${min} años`;
  }
  if (max !== null) {
    return `Hasta ${max} años`;
  }
  return 'Sin restricción por edad';
};




